// Key changes: Remove cleanup at start of useEffect, add guard flags to prevent duplicate listeners

import { useState, useEffect, useRef } from 'react';
import { Camera, Shield } from 'lucide-react';
import {
  signInAnonymously,
  onAuthStateChanged,
  signInWithCustomToken,
  signInWithPopup,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  sendPasswordResetEmail,
  sendEmailVerification,
  signOut as firebaseSignOut,
  User as FirebaseUser,
} from 'firebase/auth';
import {
  collection,
  addDoc,
  deleteDoc,
  doc,
  setDoc,
  onSnapshot,
  query,
  where,
  getDocs,
  getDoc,
  serverTimestamp,
  Timestamp,
} from 'firebase/firestore';
import { auth, db, appId, googleProvider } from '@/lib/firebase';
import {
  isRateLimited,
  recordSignInAttempt,
  getRateLimitResetTime,
  formatRateLimitTime,
  cacheSession,
  getCachedSession,
  clearSessionCache,
  cachePhoto,
  getCachedPhoto,
  clearPhotoCache,
  updateCachedPhoto,
} from '@/lib/auth-cache';
import { Header } from './components/Header';
import { Footer } from './components/Footer';
import { ProfileModal } from './components/ProfileModal';
import { SubmitModal } from './components/SubmitModal';
import { ReportCard } from './components/ReportCard';
import { EmptyState } from './components/EmptyState';
import { ScrollToTop } from './components/ScrollToTop';
import { Toast } from './components/ui/Toast';
import { ConfirmationModal } from './components/ui/ConfirmationModal';
import { AdminPanel } from './components/AdminPanel';
import { TermsOfService } from './components/LegalPages/TermsOfService';
import { PrivacyPolicy } from './components/LegalPages/PrivacyPolicy';
import { TermsOfServicePage } from './pages/TermsOfServicePage';
import { PrivacyPolicyPage } from './pages/PrivacyPolicyPage';
import { AboutPage } from './pages/AboutPage';
import { OfflineNotice } from './components/OfflineNotice';
import { useOnlineStatus } from './hooks/useOnlineStatus';

import { ReportCategory } from '@/types/reports';
import { setVerifyPHMetaTags, setPageTitle, setMetaDescription, setOpenGraphTags, setTwitterCardTags } from '@/utils/metaTags';

interface UserProfile {
  displayName: string;
  photoUrl?: string | null;
}

interface ReportData {
  id: string;
  politician?: string; // Optional for non-ANTI_EPAL categories
  location: string;
  description?: string;
  imageUrl?: string;
  authorName?: string;
  authorPhoto?: string | null;
  createdAt?: Timestamp | Date;
  userId?: string;
  status?: 'pending' | 'approved' | 'rejected';
  isPublic?: boolean;
  rejectionReason?: string;
  reviewedBy?: string;
  reviewedAt?: Timestamp | Date;
  reporter?: {
    userId: string;
    displayName?: string;
    photoUrl?: string | null;
  };
  // Phase 2: Report category
  reportCategory?: ReportCategory;
  // DILG Anti-Epal Drive fields (for ANTI_EPAL category)
  isGovernmentFunded?: boolean;
  projectType?: 'government_project' | 'government_program' | 'government_activity' | 'government_property';
  projectName?: string;
  fundingSource?: 'national' | 'local' | 'both';
  violationType?: 'name' | 'image' | 'likeness' | 'combination';
  // Additional fields for other categories
  expectedTimeline?: string;
  serviceType?: string;
  complianceType?: string;
  geo?: {
    lat: number;
    lng: number;
    accuracy?: number;
  } | null;
  imageMeta?: {
    width: number;
    height: number;
    sizeBytes: number;
    mimeType: string;
  } | null;
  communitySignals?: {
    validVotes: number;
    invalidVotes: number;
    abuseFlags: number;
    commentsCount: number;
  };
}

interface Notification {
  message: string;
  type: 'success' | 'error' | 'info';
}

interface ConfirmationState {
  isOpen: boolean;
  title: string;
  message: string;
  onConfirm: () => void;
}

interface AdminData {
  role: 'root' | 'admin';
  email: string;
  displayName: string;
  photoUrl: string | null;
  isActive: boolean;
  createdAt: Timestamp | Date;
  createdBy: string | null;
  updatedAt: Timestamp | Date;
  updatedBy: string | null;
}

interface BrandingSettings {
  brandName?: string;
  brandTagline?: string;
  logoUrl?: string;
  metaDescription?: string;
}

function App() {
  const [user, setUser] = useState<FirebaseUser | null>(null);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [reports, setReports] = useState<ReportData[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [adminRole, setAdminRole] = useState<'root' | 'admin' | null>(null);
  const isOnline = useOnlineStatus();
  const [showAdminPanel, setShowAdminPanel] = useState(false);
  const [showTerms, setShowTerms] = useState(false);
  const [showPrivacy, setShowPrivacy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [notification, setNotification] = useState<Notification | null>(null);
  const [confirmation, setConfirmation] = useState<ConfirmationState>({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: () => {},
  });
  const [allowAnonymousSignIn, setAllowAnonymousSignIn] = useState<boolean | null>(null); // null = not loaded yet
  const [settingsLoaded, setSettingsLoaded] = useState(false);
  const previousSettingRef = useRef<boolean | null>(null);
  const [branding, setBranding] = useState<BrandingSettings | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<ReportCategory | 'all'>('all');

  // Fetch admin settings for anonymous sign-in
  useEffect(() => {
    const settingsRef = doc(db, 'artifacts', appId, 'settings', 'auth');
    const unsubscribe = onSnapshot(
      settingsRef,
      (docSnap) => {
        if (docSnap.exists()) {
          const settings = docSnap.data();
          const allowAnonymous = settings.allowAnonymousSignIn ?? true;
          const previousValue = previousSettingRef.current;
          previousSettingRef.current = allowAnonymous;
          setAllowAnonymousSignIn(allowAnonymous);
          setSettingsLoaded(true);
          console.log('[Auth] Admin settings loaded:', { 
            allowAnonymousSignIn: allowAnonymous, 
            previousValue,
            settings 
          });
          
          // If setting changed from enabled to disabled, sign out anonymous users
          if (previousValue === true && allowAnonymous === false && user?.isAnonymous) {
            console.log('[Auth] Anonymous sign-in was disabled, signing out anonymous user');
            firebaseSignOut(auth).catch((error: Error) => {
              console.error('[Auth] Sign-out failed:', error);
            });
          }
        } else {
          // Default to enabled if settings don't exist
          const defaultValue = true;
          previousSettingRef.current = defaultValue;
          setAllowAnonymousSignIn(defaultValue);
          setSettingsLoaded(true);
          console.log('[Auth] No admin settings found, using defaults (enabled)');
        }
      },
      (error) => {
        console.error('[Auth] Error fetching admin settings:', error);
        // Default to enabled on error
        const defaultValue = true;
        previousSettingRef.current = defaultValue;
        setAllowAnonymousSignIn(defaultValue);
        setSettingsLoaded(true);
        console.log('[Auth] Error loading settings, defaulting to enabled');
      }
    );
    return () => unsubscribe();
  }, [user]);

  // Initialize meta tags on mount (before branding loads)
  useEffect(() => {
    const baseUrl = window.location.origin;
    setVerifyPHMetaTags({
      url: baseUrl,
      image: `${baseUrl}/og-image.png`,
    });
  }, []);

  // Branding settings (brand name, tagline, logo, meta description)
  useEffect(() => {
    const brandingRef = doc(db, 'artifacts', appId, 'settings', 'branding');

    const unsubscribe = onSnapshot(
      brandingRef,
      (docSnap) => {
        if (docSnap.exists()) {
          const data = docSnap.data() as BrandingSettings;
          setBranding(data);
          console.log('[Branding] Loaded branding settings from Firestore:', data);
        } else {
          // Provide sensible defaults if branding doc does not exist
          const defaultBranding: BrandingSettings = {
            brandName: 'VerifyPH',
            brandTagline: 'Citizen Reporting & Project Verification',
          };
          setBranding(defaultBranding);
          console.log(
            '[Branding] No branding document found, using default branding values:',
            defaultBranding
          );
        }
      },
      (error) => {
        console.error('[Branding] Error fetching branding settings:', error);
        // On error, fall back to hard-coded defaults
        setBranding({
          brandName: 'VerifyPH',
          brandTagline: 'Citizen Reporting & Project Verification',
        });
      }
    );

    return () => unsubscribe();
  }, []);

  // Keep document title and meta tags in sync with branding
  useEffect(() => {
    // Get base URL for OpenGraph
    const baseUrl = window.location.origin;
    const ogImage = `${baseUrl}/og-image.png`;
    
    if (branding) {
      // Use branding if available
      const title = branding.brandName
        ? `${branding.brandName} — ${branding.brandTagline || 'Citizen Reporting & Project Verification'}`
        : 'VerifyPH — Transparency by the People, for the People';
      
      const description = branding.metaDescription || 
        'VerifyPH is a citizen-powered transparency platform for documenting and verifying government-funded projects and public services through evidence-based reporting.';
      
      // Set title and description
      setPageTitle(title);
      setMetaDescription(description);
      
      // Set OpenGraph tags
      setOpenGraphTags({
        title: branding.brandName 
          ? `${branding.brandName} — Citizen-Powered Transparency`
          : 'VerifyPH — Citizen-Powered Transparency',
        description: description,
        type: 'website',
        url: baseUrl,
        image: ogImage,
      });
      
      // Set Twitter Card tags
      setTwitterCardTags({
        card: 'summary_large_image',
        title: branding.brandName
          ? `${branding.brandName} — Transparency by the People`
          : 'VerifyPH — Transparency by the People',
        description: description,
        image: ogImage,
      });
    } else {
      // Use default VerifyPH meta tags
      setVerifyPHMetaTags({
        url: baseUrl,
        image: ogImage,
      });
    }
  }, [branding]);

  // Auth Init with caching
  useEffect(() => {
    let hasInitialized = false;
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      console.log('[Auth] Auth state changed:', user ? `${user.uid} (anonymous: ${user.isAnonymous})` : 'null');
      setUser(user);
      
      if (!user) {
        setUserProfile(null);
        setIsAdmin(false);
        setAdminRole(null);
        
        // Only attempt anonymous sign-in if:
        // 1. Settings have been loaded
        // 2. Anonymous sign-in is enabled
        // 3. We've already initialized (to avoid immediate sign-in on mount)
        // 4. No custom auth token is being used
        if (
          hasInitialized && 
          !import.meta.env.VITE_INITIAL_AUTH_TOKEN && 
          settingsLoaded && 
          allowAnonymousSignIn === true
        ) {
          console.log('[Auth] No user after persistence check, signing in anonymously (settings allow it)');
          await signInAnonymously(auth).catch((error: Error) => {
            console.error('[Auth] Anonymous sign-in failed:', error);
          });
        } else if (hasInitialized && settingsLoaded && allowAnonymousSignIn === false) {
          console.log('[Auth] Anonymous sign-in is disabled, user must sign in with Google or Email');
        } else if (!settingsLoaded) {
          console.log('[Auth] Waiting for settings to load before attempting anonymous sign-in');
        }
      } else {
        // Cache successful session
        cacheSession(user.uid);
        
        // If anonymous sign-in is disabled and user is anonymous, sign them out
        if (settingsLoaded && allowAnonymousSignIn === false && user.isAnonymous) {
          console.log('[Auth] Anonymous sign-in disabled, signing out anonymous user');
          await firebaseSignOut(auth).catch((error: Error) => {
            console.error('[Auth] Sign-out failed:', error);
          });
        }
      }
      
      hasInitialized = true;
      // Only set loading to false after both auth state is known AND settings are loaded
      if (settingsLoaded) {
        setLoading(false);
      }
    });
    
    const initialAuthToken = import.meta.env.VITE_INITIAL_AUTH_TOKEN;
    if (initialAuthToken) {
      console.log('[Auth] Using custom auth token');
      signInWithCustomToken(auth, initialAuthToken).catch((error: Error) => {
        console.error('[Auth] Custom token sign-in failed:', error);
      });
    }
    
    return () => unsubscribe();
  }, [allowAnonymousSignIn, settingsLoaded]);

  // Admin Role Fetching - robust validation with fallback to direct fetch
  const adminUnsubscribeRef = useRef<(() => void) | null>(null);
  const adminCheckAttemptRef = useRef(0);
  
  useEffect(() => {
    if (!user) {
      setIsAdmin(false);
      setAdminRole(null);
      // Clean up existing listener
      if (adminUnsubscribeRef.current) {
        adminUnsubscribeRef.current();
        adminUnsubscribeRef.current = null;
      }
      adminCheckAttemptRef.current = 0;
      return;
    }
    
    // If listener already exists, don't create a new one
    if (adminUnsubscribeRef.current) {
      console.log('[Admin] Listener already exists, skipping duplicate setup');
      return;
    }
    
    adminCheckAttemptRef.current += 1;
    const attemptNumber = adminCheckAttemptRef.current;
    
    // Construct admin document path
    const adminPath = `artifacts/${appId}/admins/${user.uid}`;
    console.log(`[Admin] Attempt #${attemptNumber} - Checking admin status at path: ${adminPath}`);
    console.log(`[Admin] User ID: ${user.uid}`);
    console.log(`[Admin] User email: ${user.email}`);
    console.log(`[Admin] App ID: ${appId}`);
    
    const adminRef = doc(db, 'artifacts', appId, 'admins', user.uid);
    
    // First, try a direct fetch to verify document exists by UID
    const checkAdminDirect = async () => {
      try {
        console.log('[Admin] Attempting direct fetch of admin document by UID...');
        const adminSnap = await getDoc(adminRef);
        
        console.log('[Admin] Direct fetch completed');
        console.log('[Admin] Document exists:', adminSnap.exists());
        
        if (adminSnap.exists()) {
          const adminData = adminSnap.data() as AdminData;
          console.log('[Admin] Direct fetch - Admin data:', {
            role: adminData.role,
            isActive: adminData.isActive,
            email: adminData.email,
            displayName: adminData.displayName,
          });
          
          if (adminData.isActive) {
            console.log(`[Admin] ✅ Direct fetch confirmed: User is active ${adminData.role} admin`);
            setIsAdmin(true);
            setAdminRole(adminData.role);
            return true; // Found by UID
          } else {
            console.log('[Admin] ⚠️ Direct fetch: User is admin but not active');
            setIsAdmin(false);
            setAdminRole(null);
            return false;
          }
        } else {
          // Document not found by UID, try querying by email
          console.log('[Admin] ℹ️ No admin document found by UID, querying by email...');
          
          if (!user.email) {
            console.log('[Admin] ⚠️ User has no email, cannot query by email');
            setIsAdmin(false);
            setAdminRole(null);
            return false;
          }
          
          // Query admins collection by email
          const adminsRef = collection(db, 'artifacts', appId, 'admins');
          const emailQuery = query(adminsRef, where('email', '==', user.email));
          const emailQuerySnapshot = await getDocs(emailQuery);
          
          if (!emailQuerySnapshot.empty) {
            // Found admin document by email
            const emailDoc = emailQuerySnapshot.docs[0];
            const adminData = emailDoc.data() as AdminData;
            
            console.log('[Admin] ✅ Found admin document by email:', {
              docId: emailDoc.id,
              role: adminData.role,
              isActive: adminData.isActive,
              email: adminData.email,
              displayName: adminData.displayName,
            });
            
            if (adminData.isActive) {
              // Migrate document to use UID as document ID for better performance
              console.log('[Admin] Migrating admin document to use UID as document ID...');
              try {
                await setDoc(adminRef, {
                  ...adminData,
                  updatedAt: serverTimestamp(),
                  updatedBy: user.uid,
                });
                
                // Delete the old document if it's not already the UID-based one
                if (emailDoc.id !== user.uid) {
                  await deleteDoc(doc(db, 'artifacts', appId, 'admins', emailDoc.id));
                  console.log('[Admin] ✅ Migrated admin document to UID-based ID');
                }
              } catch (migrationError) {
                console.error('[Admin] ⚠️ Error migrating admin document:', migrationError);
                // Continue anyway - we found the admin, just couldn't migrate
              }
              
              console.log(`[Admin] ✅ User is active ${adminData.role} admin (found by email)`);
              setIsAdmin(true);
              setAdminRole(adminData.role);
              return true;
            } else {
              console.log('[Admin] ⚠️ User is admin but not active (found by email)');
              setIsAdmin(false);
              setAdminRole(null);
              return false;
            }
          } else {
            console.log('[Admin] ℹ️ No admin document found by email either');
            setIsAdmin(false);
            setAdminRole(null);
            return false;
          }
        }
      } catch (error) {
        console.error('[Admin] ❌ Direct fetch error:', error);
        setIsAdmin(false);
        setAdminRole(null);
        return false;
      }
    };
    
    // Execute direct check first, then set up listener after migration completes
    checkAdminDirect().then(() => {
      // Set up real-time listener for admin status changes
      // The document should now be at the UID path after migration (if found)
      console.log('[Admin] Setting up real-time listener for admin status');
      
      adminUnsubscribeRef.current = onSnapshot(
        adminRef,
        (docSnap) => {
          console.log('[Admin] Listener snapshot received');
          console.log('[Admin] Document exists:', docSnap.exists());
          console.log('[Admin] Document path:', docSnap.ref.path);
          
          if (docSnap.exists()) {
            const adminData = docSnap.data() as AdminData;
            console.log('[Admin] Listener - Admin data:', {
              role: adminData.role,
              isActive: adminData.isActive,
              email: adminData.email,
              displayName: adminData.displayName,
            });
            
            if (adminData.isActive) {
              console.log(`[Admin] ✅ Listener confirmed: User is active ${adminData.role} admin`);
              setIsAdmin(true);
              setAdminRole(adminData.role);
            } else {
              console.log('[Admin] ⚠️ Listener: User is admin but not active');
              setIsAdmin(false);
              setAdminRole(null);
            }
          } else {
            // Document doesn't exist at UID path - might need to query by email again
            console.log('[Admin] ℹ️ Listener: No admin document found at UID path');
            if (user.email) {
              // Try querying by email as fallback
              const adminsRef = collection(db, 'artifacts', appId, 'admins');
              const emailQuery = query(adminsRef, where('email', '==', user.email));
              getDocs(emailQuery).then((snapshot) => {
                if (!snapshot.empty) {
                  const emailDoc = snapshot.docs[0];
                  const adminData = emailDoc.data() as AdminData;
                  if (adminData.isActive) {
                    console.log(`[Admin] ✅ Found active admin by email via listener fallback`);
                    setIsAdmin(true);
                    setAdminRole(adminData.role);
                  } else {
                    setIsAdmin(false);
                    setAdminRole(null);
                  }
                } else {
                  setIsAdmin(false);
                  setAdminRole(null);
                }
              }).catch((error) => {
                console.error('[Admin] Error querying by email in listener fallback:', error);
                setIsAdmin(false);
                setAdminRole(null);
              });
            } else {
              setIsAdmin(false);
              setAdminRole(null);
            }
          }
        },
        (error) => {
          console.error('[Admin] ❌ Listener error:', error);
          console.error('[Admin] Error code:', error.code);
          console.error('[Admin] Error message:', error.message);
          console.error('[Admin] Full error:', JSON.stringify(error, null, 2));
          
          // On listener error, try direct fetch as fallback
          console.log('[Admin] Listener failed, attempting fallback direct fetch...');
          checkAdminDirect().catch((fallbackError) => {
            console.error('[Admin] Fallback direct fetch also failed:', fallbackError);
            setIsAdmin(false);
            setAdminRole(null);
          });
        }
      );
    }).catch((error) => {
      console.error('[Admin] Direct check failed:', error);
      setIsAdmin(false);
      setAdminRole(null);
    });
    
    return () => {
      console.log('[Admin] Cleaning up admin listener');
      if (adminUnsubscribeRef.current) {
        adminUnsubscribeRef.current();
        adminUnsubscribeRef.current = null;
      }
    };
  }, [user]);

  // Profile Fetching - prevent duplicate listeners
  const profileUnsubscribeRef = useRef<(() => void) | null>(null);
  useEffect(() => {
    if (!user) {
      setUserProfile(null);
      // Clean up existing listener
      if (profileUnsubscribeRef.current) {
        profileUnsubscribeRef.current();
        profileUnsubscribeRef.current = null;
      }
      return;
    }
    
    // If listener already exists, don't create a new one
    if (profileUnsubscribeRef.current) {
      return;
    }

    // Check for cached photo first
    const cachedPhotoUrl = getCachedPhoto(user.uid);
    
    const profileRef = doc(db, 'artifacts', appId, 'users', user.uid, 'profile', 'main');
    
    profileUnsubscribeRef.current = onSnapshot(
      profileRef,
      (docSnap) => {
        let finalPhotoUrl: string | null = null;

        if (docSnap.exists()) {
          const profileData = docSnap.data() as UserProfile;
          finalPhotoUrl = profileData.photoUrl || user.photoURL || null;
          
          setUserProfile({
            displayName: profileData.displayName || user.displayName || 'Anonymous',
            photoUrl: cachedPhotoUrl || finalPhotoUrl,
          });
        } else {
          if (user.displayName || user.photoURL) {
            finalPhotoUrl = user.photoURL || null;
            
            setUserProfile({
              displayName: user.displayName || 'Anonymous',
              photoUrl: cachedPhotoUrl || finalPhotoUrl,
            });
          } else {
            setUserProfile(null);
          }
        }

        // Update photo cache if we have a new photo URL
        if (finalPhotoUrl) {
          updateCachedPhoto(user.uid, finalPhotoUrl);
        }
      },
      (error) => {
        console.error('[Profile] Error:', error);
        if (user.displayName || user.photoURL) {
          const fallbackPhotoUrl = cachedPhotoUrl || user.photoURL || null;
          
          setUserProfile({
            displayName: user.displayName || 'Anonymous',
            photoUrl: fallbackPhotoUrl,
          });
        }
      }
    );
    
    return () => {
      if (profileUnsubscribeRef.current) {
        profileUnsubscribeRef.current();
        profileUnsubscribeRef.current = null;
      }
    };
  }, [user]);

  // Reports Data Fetching – Skip when admin panel is open to avoid duplicate listeners
  useEffect(() => {
    // Don't fetch reports if admin panel is open - admin components have their own listeners
    if (showAdminPanel) {
      return;
    }

    const fetchReports = async () => {
      try {
        const q = query(collection(db, 'artifacts', appId, 'public', 'data', 'reports'));
        const snapshot = await getDocs(q);

        const fetchedReports = snapshot.docs.map((docSnap) => ({
          id: docSnap.id,
          ...docSnap.data(),
        })) as ReportData[];

        // Non-admins only see approved and public reports
        let filteredReports = isAdmin
          ? fetchedReports
          : fetchedReports.filter((report) => {
              const status = report.status;
              const isPublic = report.isPublic !== false;
              return status === 'approved' && isPublic;
            });

        // Phase 2: Filter by category
        if (selectedCategory !== 'all') {
          filteredReports = filteredReports.filter((report) => {
            // Default to ANTI_EPAL for backward compatibility with existing reports
            const reportCat = report.reportCategory || 'ANTI_EPAL';
            return reportCat === selectedCategory;
          });
        }

        filteredReports.sort((a, b) => {
          const dateA =
            a.createdAt instanceof Timestamp
              ? a.createdAt.toDate()
              : a.createdAt instanceof Date
                ? a.createdAt
                : new Date();
          const dateB =
            b.createdAt instanceof Timestamp
              ? b.createdAt.toDate()
              : b.createdAt instanceof Date
                ? b.createdAt
                : new Date();
          return dateB.getTime() - dateA.getTime();
        });

        setReports(filteredReports);
      } catch (error) {
        console.error('[Reports] Error fetching reports:', error);
      } finally {
        setLoading(false);
      }
    };

    void fetchReports();
  }, [showAdminPanel, isAdmin, selectedCategory]);

  const showNotify = (message: string, type: 'success' | 'error' | 'info' = 'success') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 3000);
  };

  const confirmAction = (title: string, message: string, action: () => void) => {
    setConfirmation({ isOpen: true, title, message, onConfirm: action });
  };

  const handleDeleteReport = (reportId: string) => {
    confirmAction('Delete Report?', 'Are you sure you want to permanently delete this report?', async () => {
      try {
        await deleteDoc(doc(db, 'artifacts', appId, 'public', 'data', 'reports', reportId));
        showNotify('Report deleted.', 'success');
      } catch (error) {
        console.error(error);
        showNotify('Failed to delete.', 'error');
      }
    });
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (window as any).handleAddSample = () => {
    confirmAction('Generate Sample Report?', 'This will add a dummy report.', async () => {
      try {
        const canvas = document.createElement('canvas');
        canvas.width = 600;
        canvas.height = 400;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;
        ctx.fillStyle = '#e2e8f0';
        ctx.fillRect(0, 0, 600, 400);
        ctx.fillStyle = '#64748b';
        ctx.font = 'bold 30px sans-serif';
        ctx.textAlign = 'center';
        ctx.fillText('SAMPLE EVIDENCE', 300, 180);

        await addDoc(collection(db, 'artifacts', appId, 'public', 'data', 'reports'), {
          politician: 'Mayor Juan Sample',
          location: 'Public Market, San Pedro',
          description: 'Huge tarpaulin covering entrance. Verified violation.',
          imageUrl: canvas.toDataURL('image/jpeg', 0.5),
          userId: 'system_sample',
          authorName: 'System Admin',
          createdAt: serverTimestamp(),
          likes: 0,
          status: 'pending',
          isPublic: false,
          reporter: {
            userId: 'system_sample',
            displayName: 'System Admin',
            photoUrl: null,
          },
          imageMeta: {
            width: 600,
            height: 400,
            sizeBytes: 0,
            mimeType: 'image/jpeg',
          },
          communitySignals: {
            validVotes: 0,
            abuseFlags: 0,
            commentsCount: 0,
          },
        });
        showNotify('Sample added!', 'success');
      } catch (error) {
        console.error(error);
        showNotify('Failed.', 'error');
      }
    });
  };

  const handleGoogleSignIn = async () => {
    try {
      // Check if already signed in with Google (not anonymous)
      if (user && !user.isAnonymous) {
        const cachedSessionId = getCachedSession(user.uid);
        if (cachedSessionId) {
          console.log('[Auth] User already signed in with Google');
          showNotify('You are already signed in', 'info');
          return;
        }
      }

      // Check rate limiting before attempting sign-in
      if (isRateLimited()) {
        const resetTime = getRateLimitResetTime();
        const timeDisplay = formatRateLimitTime(resetTime);
        showNotify(
          `Too many sign-in attempts. Please wait ${timeDisplay} before trying again.`,
          'error'
        );
        console.warn('[Auth] Rate limit exceeded for Google sign-in');
        return;
      }

      // Record this attempt for rate limiting
      recordSignInAttempt();

      // Proceed with Google sign-in
      const result = await signInWithPopup(auth, googleProvider);
      const signedInUser = result.user;
      
      let photoUrl: string | null = null;
      if (signedInUser.photoURL) {
        photoUrl = signedInUser.photoURL;
      }
      
      await setDoc(
        doc(db, 'artifacts', appId, 'users', signedInUser.uid, 'profile', 'main'),
        {
          displayName: signedInUser.displayName || 'Anonymous',
          photoUrl,
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );

      // Cache the successful session
      cacheSession(signedInUser.uid);

      // Cache the user's photo
      if (photoUrl) {
        cachePhoto(signedInUser.uid, photoUrl);
      }
      
      showNotify('Signed in with Google successfully', 'success');
    } catch (error: unknown) {
      console.error('[Auth] Google sign-in error:', error);
      const firebaseError = error as { code?: string; message?: string };
      
      if (firebaseError.code === 'auth/popup-closed-by-user') {
        showNotify('Sign-in cancelled', 'info');
      } else if (firebaseError.code === 'auth/too-many-requests') {
        // Handle Firebase rate limiting specifically
        const resetTime = getRateLimitResetTime();
        const timeDisplay = formatRateLimitTime(resetTime);
        showNotify(
          `Too many sign-in requests. Please wait ${timeDisplay} before trying again.`,
          'error'
        );
      } else if (firebaseError.code === 'auth/network-request-failed') {
        showNotify('Network error. Please check your connection and try again.', 'error');
      } else {
        showNotify('Failed to sign in with Google. Please try again later.', 'error');
      }
    }
  };

  const handleEmailSignIn = async (email: string, password: string) => {
    try {
      // Check rate limiting before attempting sign-in
      if (isRateLimited()) {
        const resetTime = getRateLimitResetTime();
        const timeDisplay = formatRateLimitTime(resetTime);
        showNotify(
          `Too many sign-in attempts. Please wait ${timeDisplay} before trying again.`,
          'error'
        );
        console.warn('[Auth] Rate limit exceeded for email sign-in');
        return;
      }

      // Record this attempt for rate limiting
      recordSignInAttempt();

      // Sign in with email and password
      const result = await signInWithEmailAndPassword(auth, email, password);
      const signedInUser = result.user;

      // Create or update user profile in Firestore
      await setDoc(
        doc(db, 'artifacts', appId, 'users', signedInUser.uid, 'profile', 'main'),
        {
          displayName: signedInUser.displayName || signedInUser.email?.split('@')[0] || 'User',
          photoUrl: null,
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );

      // Cache the successful session
      cacheSession(signedInUser.uid);

      showNotify('Signed in successfully', 'success');
    } catch (error: unknown) {
      console.error('[Auth] Email sign-in error:', error);
      const firebaseError = error as { code?: string; message?: string };

      if (firebaseError.code === 'auth/invalid-credential' || firebaseError.code === 'auth/user-not-found' || firebaseError.code === 'auth/wrong-password') {
        showNotify('Invalid email or password. Please try again.', 'error');
      } else if (firebaseError.code === 'auth/too-many-requests') {
        const resetTime = getRateLimitResetTime();
        const timeDisplay = formatRateLimitTime(resetTime);
        showNotify(
          `Too many sign-in requests. Please wait ${timeDisplay} before trying again.`,
          'error'
        );
      } else if (firebaseError.code === 'auth/network-request-failed') {
        showNotify('Network error. Please check your connection and try again.', 'error');
      } else {
        showNotify('Failed to sign in. Please try again later.', 'error');
      }
    }
  };

  const handleEmailRegister = async (email: string, password: string, displayName: string) => {
    try {
      // Check rate limiting before attempting registration
      if (isRateLimited()) {
        const resetTime = getRateLimitResetTime();
        const timeDisplay = formatRateLimitTime(resetTime);
        showNotify(
          `Too many sign-in attempts. Please wait ${timeDisplay} before trying again.`,
          'error'
        );
        console.warn('[Auth] Rate limit exceeded for email registration');
        return;
      }

      // Record this attempt for rate limiting
      recordSignInAttempt();

      // Create user with email and password
      const result = await createUserWithEmailAndPassword(auth, email, password);
      const newUser = result.user;

      // Send email verification
      try {
        await sendEmailVerification(newUser);
        console.log('[Auth] Verification email sent to:', email);
      } catch (verifyError) {
        console.error('[Auth] Failed to send verification email:', verifyError);
        // Continue even if verification email fails
      }

      // Create user profile in Firestore
      await setDoc(
        doc(db, 'artifacts', appId, 'users', newUser.uid, 'profile', 'main'),
        {
          displayName: displayName.trim(),
          photoUrl: null,
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );

      // Cache the successful session
      cacheSession(newUser.uid);

      showNotify(
        'Account created successfully! Please check your email to verify your account.',
        'success'
      );
    } catch (error: unknown) {
      console.error('[Auth] Email registration error:', error);
      const firebaseError = error as { code?: string; message?: string };

      if (firebaseError.code === 'auth/email-already-in-use') {
        showNotify('This email is already registered. Please sign in instead.', 'error');
      } else if (firebaseError.code === 'auth/invalid-email') {
        showNotify('Invalid email address. Please check and try again.', 'error');
      } else if (firebaseError.code === 'auth/weak-password') {
        showNotify('Password is too weak. Please use a stronger password.', 'error');
      } else if (firebaseError.code === 'auth/too-many-requests') {
        const resetTime = getRateLimitResetTime();
        const timeDisplay = formatRateLimitTime(resetTime);
        showNotify(
          `Too many registration requests. Please wait ${timeDisplay} before trying again.`,
          'error'
        );
      } else if (firebaseError.code === 'auth/network-request-failed') {
        showNotify('Network error. Please check your connection and try again.', 'error');
      } else {
        showNotify('Failed to create account. Please try again later.', 'error');
      }
    }
  };

  const handlePasswordReset = async (email: string) => {
    try {
      // Check rate limiting before attempting password reset
      if (isRateLimited()) {
        const resetTime = getRateLimitResetTime();
        const timeDisplay = formatRateLimitTime(resetTime);
        showNotify(
          `Too many requests. Please wait ${timeDisplay} before trying again.`,
          'error'
        );
        console.warn('[Auth] Rate limit exceeded for password reset');
        return;
      }

      // Record this attempt for rate limiting
      recordSignInAttempt();

      // Send password reset email
      await sendPasswordResetEmail(auth, email);

      showNotify(
        'Password reset email sent! Please check your inbox and follow the instructions.',
        'success'
      );
      console.log('[Auth] Password reset email sent to:', email);
    } catch (error: unknown) {
      console.error('[Auth] Password reset error:', error);
      const firebaseError = error as { code?: string; message?: string };

      if (firebaseError.code === 'auth/user-not-found') {
        // Don't reveal if user exists (security best practice)
        showNotify(
          'If an account exists with this email, you will receive a password reset link.',
          'info'
        );
      } else if (firebaseError.code === 'auth/invalid-email') {
        showNotify('Invalid email address. Please check and try again.', 'error');
      } else if (firebaseError.code === 'auth/too-many-requests') {
        const resetTime = getRateLimitResetTime();
        const timeDisplay = formatRateLimitTime(resetTime);
        showNotify(
          `Too many reset requests. Please wait ${timeDisplay} before trying again.`,
          'error'
        );
      } else if (firebaseError.code === 'auth/network-request-failed') {
        showNotify('Network error. Please check your connection and try again.', 'error');
      } else {
        showNotify('Failed to send reset email. Please try again later.', 'error');
      }
    }
  };

  const handleSignOut = async () => {
    try {
      // Clear cached session and photo before signing out
      if (user) {
        clearSessionCache(user.uid);
        clearPhotoCache(user.uid);
      }

      await firebaseSignOut(auth);
      setIsAdmin(false);
      showNotify('Signed out successfully', 'info');
    } catch (error) {
      console.error('[Auth] Sign-out error:', error);
      showNotify('Failed to sign out', 'error');
    }
  };

  const handleSaveProfile = async (displayName: string) => {
    if (!user) return;
    const photoUrl = user.photoURL || userProfile?.photoUrl || null;
    await setDoc(
      doc(db, 'artifacts', appId, 'users', user.uid, 'profile', 'main'),
      { displayName, photoUrl, updatedAt: serverTimestamp() },
      { merge: true }
    );
  };

  const requireProfile = () => {
    showNotify('Please create a profile first', 'info');
    setIsProfileOpen(true);
  };

  useEffect(() => {
    if (showAdminPanel && (!user || !isAdmin || !adminRole)) {
      setShowAdminPanel(false);
      showNotify('Access denied. Admin privileges required.', 'error');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showAdminPanel, user, isAdmin, adminRole]);

  const currentPath = window.location.pathname;
  if (currentPath === '/privacy') return <PrivacyPolicyPage />;
  if (currentPath === '/terms') return <TermsOfServicePage />;
  if (currentPath === '/about') return <AboutPage />;

  if (showAdminPanel && user && isAdmin && adminRole) {
    return (
      <>
        <OfflineNotice isOnline={isOnline} />
        <AdminPanel
          currentUser={user}
          currentUserRole={adminRole}
          onBackToHome={() => setShowAdminPanel(false)}
          userProfile={userProfile}
          showNotify={showNotify}
          confirmAction={confirmAction}
        />
        <Toast notification={notification} onClose={() => setNotification(null)} />
        <ConfirmationModal 
          isOpen={confirmation.isOpen} 
          title={confirmation.title} 
          message={confirmation.message} 
          onConfirm={confirmation.onConfirm} 
          onClose={() => setConfirmation((prev) => ({ ...prev, isOpen: false }))} 
        />
      </>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 font-sans text-gray-900 pb-20">
      <OfflineNotice isOnline={isOnline} />
      <Header
        onReportClick={() => {
          if (userProfile) setIsModalOpen(true);
          else requireProfile();
        }}
        user={user}
        userProfile={userProfile}
        onProfileClick={() => setIsProfileOpen(true)}
        branding={branding || undefined}
      />

      <main id="main-content" className="max-w-2xl mx-auto px-4 py-6" role="main" aria-label="Citizen Reports feed">
        {/* Phase 2: Global Disclaimer */}
        <div className="mb-4 bg-gray-50 border border-gray-200 rounded-lg p-3">
          <p className="text-xs text-gray-600 text-center italic">
            VerifyPH documents citizen-submitted evidence. It does not make legal judgments, accusations, or official determinations.
          </p>
        </div>

        {/* Phase 2: Category Filter */}
        <div className="mb-4">
          <label htmlFor="category-filter" className="block text-xs font-semibold text-gray-700 mb-2">
            Filter by Category
          </label>
          <select
            id="category-filter"
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value as ReportCategory | 'all')}
            className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="all">All Categories</option>
            <option value="ANTI_EPAL">Anti-Epal Watch</option>
            <option value="PROJECT_DELAY">Project Delay</option>
            <option value="GHOST_PROJECT">Ghost Project</option>
            <option value="SERVICE_FAILURE">Service Failure</option>
            <option value="COMPLIANCE_GAP">Compliance Gap</option>
          </select>
        </div>
        
        {/* Anti-Epal Category Disclaimer */}
        {selectedCategory === 'ANTI_EPAL' && (
          <div className="mb-4 bg-blue-50 border border-blue-200 rounded-lg p-3">
            <p className="text-xs text-blue-700 italic text-center">
              Anti-Epal Watch documents observed promotional materials on government-funded projects for transparency and public reference only.
            </p>
          </div>
        )}

        <div className="mb-6 bg-blue-50 border border-blue-100 rounded-lg p-4 flex gap-3 items-start">
          <div className="bg-blue-100 p-2 rounded-full text-blue-600 shrink-0">
            <Shield size={20} />
          </div>
          <div>
            <h4 className="font-bold text-blue-900 text-sm">Community Guidelines</h4>
            <p className="text-xs text-blue-700 mt-1">
              Submit evidence-based observations with photos, videos, and location data. All submissions are reviewed by moderators before public visibility. Be civil and factual in discussions.
            </p>
          </div>
        </div>

        {/* Show authentication requirement if anonymous sign-in is disabled */}
        {settingsLoaded && allowAnonymousSignIn === false && (!user || user.isAnonymous) && (
          <div className="mb-6 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <Shield className="text-yellow-600" size={20} />
              <h4 className="font-bold text-yellow-900 text-sm">Sign-In Required</h4>
            </div>
            <p className="text-xs text-yellow-700">
              To access this portal, you must sign in with your Google account. Click your profile icon and select &quot;Sign in with Google&quot; to continue.
            </p>
          </div>
        )}

        {loading ? (
          <div className="space-y-4">
            {[1, 2].map((i) => (
              <div key={i} className="bg-white rounded-xl h-64 animate-pulse shadow-sm" />
            ))}
          </div>
        ) : reports.length > 0 ? (
          reports.map((report) => (
            <ReportCard
              key={report.id}
              data={report}
              isAdmin={isAdmin}
              currentUser={user}
              userProfile={userProfile}
              onDelete={handleDeleteReport}
              onLoginReq={requireProfile}
            />
          ))
        ) : (
          <EmptyState />
        )}
      </main>

      <SubmitModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} user={user} userProfile={userProfile} showNotify={showNotify} />
        <ProfileModal
          isOpen={isProfileOpen}
          onClose={() => setIsProfileOpen(false)}
          user={user}
          userProfile={userProfile}
          onSaveProfile={handleSaveProfile}
          onGoogleSignIn={handleGoogleSignIn}
          onEmailSignIn={handleEmailSignIn}
          onEmailRegister={handleEmailRegister}
          onPasswordReset={handlePasswordReset}
          onSignOut={handleSignOut}
          onOpenAdminPanel={() => {
            if (isAdmin && user && adminRole) {
              setShowAdminPanel(true);
              setIsProfileOpen(false);
            }
          }}
          isAdmin={isAdmin}
          adminRole={adminRole}
          showNotify={showNotify}
          allowAnonymousSignIn={allowAnonymousSignIn ?? true}
        />
      <Toast notification={notification} onClose={() => setNotification(null)} />
      <ConfirmationModal isOpen={confirmation.isOpen} title={confirmation.title} message={confirmation.message} onConfirm={confirmation.onConfirm} onClose={() => setConfirmation((prev) => ({ ...prev, isOpen: false }))} />
      <Footer brandName={branding?.brandName} brandTagline={branding?.brandTagline} />
      <TermsOfService isOpen={showTerms} onClose={() => setShowTerms(false)} />
      <PrivacyPolicy isOpen={showPrivacy} onClose={() => setShowPrivacy(false)} />
      <ScrollToTop />
      <button
        onClick={() => {
          if (userProfile) setIsModalOpen(true);
          else requireProfile();
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            if (userProfile) setIsModalOpen(true);
            else requireProfile();
          }
        }}
        className="fixed bottom-6 right-6 md:hidden bg-blue-600 hover:bg-blue-700 active:bg-blue-800 text-white p-4 rounded-full shadow-xl transition-all duration-200 active:scale-95 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:ring-offset-2 focus:ring-offset-white z-40"
        aria-label={userProfile ? 'Submit a new report' : 'Sign in to submit a report'}
        title={userProfile ? 'Submit a new report' : 'Please sign in to submit a report'}
      >
        <Camera size={24} aria-hidden="true" />
      </button>
    </div>
  );
}

export default App;
